import React, { PropsWithChildren, useEffect, useState } from 'react';

import { styled } from '@mui/material';

import { Language } from '@mewa/types';
import { ResponsiveTabs, Line as SharedLine, TextSelect } from '@mewa/ui';

import { slugifyByLang } from '../../../../libs/slugify';
import t from '../../../../libs/translate';
import { useWindowHashNavigation } from '../../../hooks/use-window-hash-navigation';

const TAB_HEIGHT = '72px';

type Props = {
  filterCriteria: string[];
  translations: {
    filter_tabs_sorting: string;
    filter_tabs_oldest_first: string;
    filter_tabs_newest_first: string;
  };
  lang: Language;
  onSortingChange: (sortOrder: number) => void;
  onFilterChange: (filterIndex: number) => void;
  WrapperComponent?: ({ children }: PropsWithChildren) => React.ReactNode;
};

export const FilterTabs = ({
  filterCriteria,
  translations,
  lang,
  onSortingChange,
  onFilterChange,
  WrapperComponent,
}: Props) => {
  const filterItems = filterCriteria.map((filterCriterion) => ({
    name: filterCriterion,
    hash: '#' + slugifyByLang(filterCriterion, lang),
  }));

  const { activeItem, handleNavigationChange } =
    useWindowHashNavigation(filterItems);
  const [sortIndex, setSortIndex] = useState<number>(0);
  const fadeDuration = 400;
  const pause = 100;

  useEffect(() => {
    onFilterChange(activeItem);
  }, [activeItem, onFilterChange]);

  const Wrapper = WrapperComponent ?? React.Fragment;

  const itemsFilter = [
    {
      name: t(translations, 'filter_tabs_newest_first'),
    },
    {
      name: t(translations, 'filter_tabs_oldest_first'),
    },
  ];

  return (
    <>
      <StickyFilterTabs>
        <Wrapper>
          <TabsAndFilter>
            <StyledResponsiveTabs
              name="category-tabs"
              selectLabel={'Themen'}
              activeIndex={activeItem}
              onChange={(value) => {
                setTimeout(() => {
                  handleNavigationChange(value);
                }, fadeDuration + pause);
              }}
              items={filterItems}
              textVariant={true}
            />
            <SortWrapper>
              <TextSelect
                id={'sort-articles'}
                label={t(translations, 'filter_tabs_sorting')}
                activeIndex={sortIndex}
                onChange={(value) => {
                  setSortIndex(value);
                  onSortingChange(value);
                }}
                items={itemsFilter}
              />
            </SortWrapper>
          </TabsAndFilter>
        </Wrapper>
      </StickyFilterTabs>
      <StickyFilterLine>
        <Wrapper>
          <FilterLine />
        </Wrapper>
      </StickyFilterLine>
    </>
  );
};

export const FilterLine = styled(SharedLine)({ margin: 0 });
export default FilterTabs;

export const StickyFilterTabs = styled('div')({
  backgroundColor: 'var(--color-white)',
  position: 'sticky',
  top: 'calc(var(--header-sticky-position) + var(--header-height))',
  transition: 'top var(--transition-duration)',
  zIndex: 2,
  width: '100%',
});

export const StickyFilterLine = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: `calc(var(--header-sticky-position) + var(--header-height) + ${TAB_HEIGHT})`,
  transition: 'top var(--transition-duration)',

  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '10px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '20px',
  },

  zIndex: 1,
  width: '100%',
}));

const TabsAndFilter = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '14px',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 0,
  height: TAB_HEIGHT,

  [theme.breakpoints.up('sm')]: {
    gap: 0,
  },
}));

const StyledResponsiveTabs = styled(ResponsiveTabs)(({ theme }) => ({
  height: '100%',
  width: '50%',

  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SortWrapper = styled('div')(({ theme }) => ({
  paddingLeft: '20px',
  width: '50%',
  height: '100%',

  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));
